import { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@services/firebase';
import { getUserDocument, updateUserDocument } from '@services/userService';
import { refreshFcmToken } from '@services/pushNotificationService';
import {
  signUpWithEmail,
  signInWithEmail,
  signInWithGoogle,
  completeGoogleSignUp,
  logOut,
  resetPassword,
} from '@services/authService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Derive the organizations list from userData, migrating from single-org fields if needed.
 */
const deriveUserOrganizations = (userData) => {
  if (!userData) return [];
  if (userData.organizations && userData.organizations.length > 0) {
    return userData.organizations;
  }
  // On-the-fly migration: synthesize from legacy single-org fields
  if (userData.organizationId) {
    return [{
      organizationId: userData.organizationId,
      organizationName: userData.organizationName || '',
      status: userData.status || 'active',
      joinedAt: userData.createdAt || null,
    }];
  }
  return [];
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeOrganizationId, setActiveOrganizationId] = useState(null);
  const [activeOrganizationName, setActiveOrganizationName] = useState(null);
  const [userOrganizations, setUserOrganizations] = useState([]);

  // Listen to Firebase Auth state changes
  useEffect(() => {
    // If Firebase auth is not initialized, set loading to false and return
    if (!auth) {
      console.warn('⚠️ Auth not initialized - running in demo mode');
      setLoading(false);
      setCurrentUser(null);
      setUserData(null);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          // User is signed in, fetch their Firestore document
          const userDoc = await getUserDocument(user.uid);
          setCurrentUser(user);
          setUserData(userDoc);
          // Silently refresh FCM token if user had push enabled
          if (userDoc?.pushNotificationsEnabled) {
            refreshFcmToken(user.uid).catch(() => {});
          }
          const orgs = deriveUserOrganizations(userDoc);
          // On-the-fly migration: write organizationIds flat array if missing
          if (userDoc && !userDoc.organizationIds && orgs.length > 0) {
            const ids = orgs.map(o => o.organizationId);
            updateUserDocument(user.uid, { organizationIds: ids }).catch(() => {});
          }
          setUserOrganizations(orgs);
          const defaultId = userDoc?.defaultOrganizationId || userDoc?.organizationId;
          const defaultOrg = orgs.find(o => o.organizationId === defaultId) || orgs[0] || null;
          setActiveOrganizationId(defaultOrg?.organizationId || null);
          setActiveOrganizationName(defaultOrg?.organizationName || userDoc?.organizationName || null);
        } else {
          // User is signed out
          setCurrentUser(null);
          setUserData(null);
          setUserOrganizations([]);
          setActiveOrganizationId(null);
          setActiveOrganizationName(null);
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Sync org state from a userDoc
  const syncOrgState = (userDoc) => {
    const orgs = deriveUserOrganizations(userDoc);
    setUserOrganizations(orgs);
    const defaultId = userDoc?.defaultOrganizationId || userDoc?.organizationId;
    const defaultOrg = orgs.find(o => o.organizationId === defaultId) || orgs[0] || null;
    // Keep current active org if still valid; otherwise fall back to default
    const stillValid = orgs.some(o => o.organizationId === activeOrganizationId);
    if (!stillValid) {
      setActiveOrganizationId(defaultOrg?.organizationId || null);
      setActiveOrganizationName(defaultOrg?.organizationName || userDoc?.organizationName || null);
    }
  };

  // Sign up with email and password
  const signup = async (email, password, additionalData) => {
    try {
      setError(null);
      setLoading(true);
      const result = await signUpWithEmail(email, password, additionalData);
      setCurrentUser(result.user);
      setUserData(result.userData);
      syncOrgState(result.userData);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with email and password
  const signin = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const result = await signInWithEmail(email, password);
      setCurrentUser(result.user);
      setUserData(result.userData);
      syncOrgState(result.userData);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Google
  const signinWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      const result = await signInWithGoogle();
      setCurrentUser(result.user);
      setUserData(result.userData);
      syncOrgState(result.userData);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Complete Google sign up (after organization selection)
  const completeGoogleSignup = async (user, additionalData) => {
    try {
      setError(null);
      setLoading(true);
      const userDoc = await completeGoogleSignUp(user, additionalData);
      setUserData(userDoc);
      syncOrgState(userDoc);
      return userDoc;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signout = async () => {
    try {
      setError(null);
      setLoading(true);
      await logOut();
      setCurrentUser(null);
      setUserData(null);
      setUserOrganizations([]);
      setActiveOrganizationId(null);
      setActiveOrganizationName(null);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const forgotPassword = async (email) => {
    try {
      setError(null);
      await resetPassword(email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Refresh user data from Firestore
  const refreshUserData = async () => {
    try {
      if (currentUser) {
        const userDoc = await getUserDocument(currentUser.uid);
        setUserData(userDoc);
        syncOrgState(userDoc);
        return userDoc;
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
      setError(err.message);
      throw err;
    }
  };

  // Switch active organization (session only, does not persist)
  const switchOrganization = (orgId, orgName) => {
    setActiveOrganizationId(orgId);
    setActiveOrganizationName(orgName);
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return currentUser !== null && userData !== null;
  };

  // Check if user is volunteer
  const isVolunteer = () => {
    return userData?.userType === 'volunteer';
  };

  // Check if user is org admin (platform admins have a superset of org admin capabilities)
  const isOrgAdmin = () => {
    return userData?.userType === 'org-admin' || userData?.userType === 'platform-admin';
  };

  // Check if user is platform admin
  const isPlatformAdmin = () => {
    return userData?.userType === 'platform-admin';
  };

  // Check if user is the owner of their organization (not a co-admin).
  // Platform admins have a superset of org-admin capabilities, so they pass too.
  const isOwner = () => {
    return userData?.userType === 'platform-admin' ||
      (userData?.userType === 'org-admin' && userData?.role === 'owner');
  };

  // Check if user has specific role (platform admins satisfy org-admin role requirements)
  const hasRole = (roles) => {
    if (!userData) return false;
    const effectiveRoles = Array.isArray(roles) ? roles : [roles];
    if (effectiveRoles.includes('org-admin') && userData.userType === 'platform-admin') return true;
    return effectiveRoles.includes(userData.userType);
  };

  // Check if user belongs to organization (checks full organizations array)
  const belongsToOrganization = (organizationId) => {
    if (userOrganizations.length > 0) {
      return userOrganizations.some(o => o.organizationId === organizationId);
    }
    return userData?.organizationId === organizationId;
  };

  const value = {
    currentUser,
    userData,
    loading,
    error,
    signup,
    signin,
    signinWithGoogle,
    completeGoogleSignup,
    signout,
    forgotPassword,
    refreshUserData,
    isAuthenticated,
    isVolunteer,
    isOrgAdmin,
    isPlatformAdmin,
    isOwner,
    hasRole,
    belongsToOrganization,
    activeOrganizationId,
    activeOrganizationName,
    userOrganizations,
    switchOrganization,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
